import {castArray, isEqual} from 'lodash';
import {ArrayRows, ComparisonCondition, Condition} from '@/model/types';
import {DOMAIN_VALUE_PREFIX} from '@/model/types/constants';
import {ModelCondition, Predicate, ValueResolver, VisibilitySet} from './types';
import {recursiveReplaceIndex} from './utils/object';
import {applyFilter} from './values/generated';

type PredicateOperator = Condition['operator'];

type PredicateFactory<T extends PredicateOperator> = (
  condition: Omit<Extract<Condition, {operator: T}>, 'operator'>,
  valueResolver: ValueResolver,
  visibilitySet: VisibilitySet,
) => Predicate;

type ComparisonOperands = {left: string | undefined; right: string | undefined};

type PredicateMap = {
  [T in PredicateOperator]: PredicateFactory<T>;
};

const createComparisonPredicateFactory =
  <T extends ComparisonCondition['operator']>(operator: T): PredicateFactory<T> =>
  (condition, valueResolver, visibilitySet) => {
    return () => {
      const {left, right} = getComparisonOperands(condition, valueResolver, visibilitySet);
      return compareValues(left, right, operator);
    };
  };

const IsEqualPredicate: PredicateFactory<'='> = createComparisonPredicateFactory('=');
const IsDifferentPredicate: PredicateFactory<'!='> = createComparisonPredicateFactory('!=');
const GreaterThanPredicate: PredicateFactory<'>'> = createComparisonPredicateFactory('>');
const GreaterThanOrEqualPredicate: PredicateFactory<'>='> = createComparisonPredicateFactory('>=');
const LessThanPredicate: PredicateFactory<'<'> = createComparisonPredicateFactory('<');
const LessThanOrEqualPredicate: PredicateFactory<'<='> = createComparisonPredicateFactory('<=');

const AndPredicate: PredicateFactory<'and'> = (condition, valueResolver, visibilitySet) => {
  return () => condition.conditions.every(c => getPredicate(c)(valueResolver, visibilitySet)());
};

const OrPredicate: PredicateFactory<'or'> = (condition, valueResolver, visibilitySet) => {
  return () => condition.conditions.some(c => getPredicate(c)(valueResolver, visibilitySet)());
};

const NotPredicate: PredicateFactory<'not'> = (condition, valueResolver, visibilitySet) => {
  return () => !getPredicate({operator: 'and', conditions: condition.conditions})(valueResolver, visibilitySet)();
};

const InPredicate: PredicateFactory<'in'> = (condition, valueResolver, visibilitySet) => {
  return () =>
    condition.value.some(v => {
      return getPredicate({operator: '=', field: condition.field, value: v})(valueResolver, visibilitySet)();
    });
};

const NotInPredicate: PredicateFactory<'not in'> = (condition, valueResolver, visibilitySet) => {
  return () =>
    !getPredicate({operator: 'in', field: condition.field, value: condition.value})(valueResolver, visibilitySet)();
};

const HasPredicate: PredicateFactory<'has'> = (condition, valueResolver, visibilitySet) => {
  return () => {
    const {left, right} = getComparisonOperands(condition, valueResolver, visibilitySet);
    const leftArray = castArray(left).map(value => {
      if (value.startsWith(DOMAIN_VALUE_PREFIX)) {
        return valueResolver({$ref: value});
      }

      return valueResolver(value);
    });
    if (right === undefined) return false;
    return leftArray.includes(right);
  };
};

const EmptyPredicate: PredicateFactory<'empty'> = (condition, valueResolver, visibilitySet) => {
  return () => condition.fields.every(f => !visibilitySet.has(f) || valueResolver({$ref: f}) == '');
};

const NotEmptyCondition: PredicateFactory<'not empty'> = (condition, valueResolver, visibilitySet) => {
  return () => condition.fields.every(f => visibilitySet.has(f) && valueResolver({$ref: f}) !== '');
};

const VisiblePredicate: PredicateFactory<'visible'> = (condition, _valueResolver, visibilitySet) => {
  return () => visibilitySet.has(condition.field);
};

const ArrayEqualPredicate: PredicateFactory<'array equal'> = (condition, valueResolver, visibilitySet) => {
  return () => {
    const [leftRows, rightRows] = condition.arrays.map((rows, side) =>
      getArrayRowValues(
        rows,
        condition.fields.map(fieldPair => fieldPair[side]),
        valueResolver,
        visibilitySet,
      ).sort(),
    );
    return isEqual(leftRows, rightRows);
  };
};

const predicateMap: PredicateMap = {
  '=': IsEqualPredicate,
  '!=': IsDifferentPredicate,
  '>': GreaterThanPredicate,
  '>=': GreaterThanOrEqualPredicate,
  '<': LessThanPredicate,
  '<=': LessThanOrEqualPredicate,
  'and': AndPredicate,
  'or': OrPredicate,
  'not': NotPredicate,
  'in': InPredicate,
  'not in': NotInPredicate,
  'has': HasPredicate,
  'empty': EmptyPredicate,
  'not empty': NotEmptyCondition,
  'visible': VisiblePredicate,
  'array equal': ArrayEqualPredicate,
};

export const getPredicate = <K extends PredicateOperator>(
  condition: Extract<Condition, {operator: K}>,
): ModelCondition => {
  if (!predicateMap[condition.operator]) {
    throw new Error(`Unknown condition operator: ${condition.operator}`);
  }

  return (valueResolver, visibilitySet) => predicateMap[condition.operator](condition, valueResolver, visibilitySet);
};

const compareValues = (valueA: unknown, valueB: unknown, operator: ComparisonCondition['operator']) => {
  switch (operator) {
    case '=': {
      const numbers = asNumbers(valueA, valueB);
      return numbers ? numbers[0] === numbers[1] : toComparableString(valueA) === toComparableString(valueB);
    }
    case '!=': {
      const numbers = asNumbers(valueA, valueB);
      return numbers ? numbers[0] !== numbers[1] : toComparableString(valueA) !== toComparableString(valueB);
    }
    case '>':
      return !!valueA && !!valueB && parseFloat(String(valueA)) > parseFloat(String(valueB));
    case '>=':
      return !!valueA && !!valueB && parseFloat(String(valueA)) >= parseFloat(String(valueB));
    case '<':
      return !!valueA && !!valueB && parseFloat(String(valueA)) < parseFloat(String(valueB));
    case '<=':
      return !!valueA && !!valueB && parseFloat(String(valueA)) <= parseFloat(String(valueB));
  }
};

const NUMERIC_STRING_REGEX = /^-?\d+(\.\d+)?$/;

const asNumbers = (valueA: unknown, valueB: unknown): [number, number] | undefined => {
  if (!valueA || !valueB) return undefined;
  const stringA = String(valueA);
  const stringB = String(valueB);
  return NUMERIC_STRING_REGEX.test(stringA) && NUMERIC_STRING_REGEX.test(stringB)
    ? [parseFloat(stringA), parseFloat(stringB)]
    : undefined;
};

const toComparableString = (value: unknown): unknown => {
  if (typeof value === 'string') {
    return value.toLowerCase();
  }

  if (typeof value === 'number') {
    return value.toString();
  }

  if (Array.isArray(value)) {
    return value.length === 0 ? '' : value.map(toComparableString);
  }

  return value;
};

const toComparableRowValue = (value: unknown): unknown => {
  const comparable = toComparableString(value);
  return typeof comparable === 'string' && NUMERIC_STRING_REGEX.test(comparable)
    ? parseFloat(comparable).toString()
    : comparable;
};

const getArrayRowValues = (
  {array, filters = []}: ArrayRows,
  fields: string[],
  valueResolver: ValueResolver,
  visibilitySet: VisibilitySet,
): string[] => {
  const options = {visibilitySet};
  const rowCount = Number(valueResolver({$ref: `_${array}[count]`}));
  if (isNaN(rowCount)) return [];

  return Array.from({length: rowCount}, (_, index) => index)
    .filter(index => recursiveReplaceIndex(filters, index).every(filter => applyFilter(filter, valueResolver, options)))
    .map(index =>
      JSON.stringify(fields.map(field => toComparableRowValue(valueResolver({$ref: field.atIndex(index)}, options)))),
    );
};

const getComparisonOperands = <T extends ComparisonCondition['operator']>(
  condition: Omit<Extract<Condition, {operator: T}>, 'operator'>,
  valueResolver: ValueResolver,
  visibilitySet: VisibilitySet,
): ComparisonOperands => {
  const options = {visibilitySet};
  return {
    left: getLeftOperand(condition, valueResolver, options),
    right: getRightOperand(condition, valueResolver, options),
  };
};

const getLeftOperand = <T extends ComparisonCondition['operator']>(
  condition: Omit<Extract<Condition, {operator: T}>, 'operator'>,
  valueResolver: ValueResolver,
  options: {visibilitySet: VisibilitySet},
) => {
  return typeof condition.field === 'string'
    ? valueResolver({$ref: condition.field}, options)
    : valueResolver(condition.field, options);
};

const getRightOperand = <T extends ComparisonCondition['operator']>(
  condition: Omit<Extract<Condition, {operator: T}>, 'operator'>,
  valueResolver: ValueResolver,
  options: {visibilitySet: VisibilitySet},
) => {
  if (typeof condition.value !== 'string' && typeof condition.value !== 'number') {
    return valueResolver(condition.value, options);
  }

  if (typeof condition.value === 'string') {
    if (condition.value.startsWith(DOMAIN_VALUE_PREFIX)) {
      return valueResolver({$ref: condition.value}, options);
    }

    return condition.value;
  }

  return condition.value.toFixed();
};
